import { defineStore } from 'pinia'
import { authenticatedFetch, backendURL } from './state'

interface Company {
  id: number
  route: string
  name: string
  category: string
  logo: string
  price: number
}

interface TestData {
  test_id: string
  category: string
  title: string  
  questions_number: number
  alloted_time: number
  guides?: Record<string, string>
  additional_metadata?: {
    difficulty?: 'easy' | 'medium' | 'hard'
    passing_score?: number
    tags?: string[]
    version?: string
  }
}

interface CompanyWithTests extends Company {
  tests: TestData[]
}

interface UserPreferences {
  company_id: number
  favourite: boolean
  permitted: boolean
}

interface PreferencesMap {
  [companyId: number]: {
    favourite: boolean
    permitted: boolean
  }
}

export const useCompaniesStore = defineStore('companies', {
  state: () => ({
    catalog: [] as Company[],
    catalogLastFetch: 0,
    catalogCacheDuration: 3600_000,
    
    preferences: {} as PreferencesMap,
    preferencesLastFetch: 0,
    preferencesCacheDuration: 5_000,
    
    companyDetailsCache: new Map<string, CompanyWithTests>(),
    companyDetailsCacheDuration: 3600_000,
    companyDetailsCacheTime: new Map<string, number>(),
    
    loading: false,
    error: null as string | null,
  }),

  getters: {
    companiesWithPreferences(): (Company & { favourite: boolean; permitted: boolean })[] {
      return this.catalog.map(company => ({
        ...company,
        favourite: this.preferences[company.id]?.favourite ?? false,
        permitted: this.preferences[company.id]?.permitted ?? false
      }))
    },
    
    companiesByCategory: (state) => (category: string) => {
      const companies = state.catalog.map(company => ({
        ...company,
        favourite: state.preferences[company.id]?.favourite ?? false,
        permitted: state.preferences[company.id]?.permitted ?? false
      }))
      
      return category === 'all' 
        ? companies 
        : companies.filter(c => c.category === category)
    },
    
    favoriteCompanies(): Company[] {
      return this.catalog.filter(c => 
        this.preferences[c.id]?.favourite === true
      )
    },

    getTestInstructions: (state) => (testId: string) => {
      for (const company of state.companyDetailsCache.values()) {
        const test = company.tests.find(t => t.test_id === testId)
        if (test) {
          return {
            test_id: test.test_id,
            title: test.title,
            instructions: '', 
            alloted_time: test.alloted_time,
            questions_number: test.questions_number,
            navigation: true,
            category: test.category,
            is_active: true,
            guides: test.guides || {} 
          }
        }
      }
      return null
    }
  },

  actions: {
    async fetchCatalog(force = false) {
      if (!force && Date.now() - this.catalogLastFetch < this.catalogCacheDuration) {
        return
      }

      this.loading = true
      try {
        const response = await authenticatedFetch(
          backendURL('companies/catalog'),
          { credentials: 'include' }
        )
        
        if (!response.ok) {
          throw new Error('Failed to fetch catalog')
        }
        
        this.catalog = await response.json()
        this.catalogLastFetch = Date.now()
    
        localStorage.setItem('companies_catalog', JSON.stringify(this.catalog))
        localStorage.setItem('companies_catalog_timestamp', String(this.catalogLastFetch))
        
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Failed to load companies'
    
        const cached = localStorage.getItem('companies_catalog')
        if (cached) {
          this.catalog = JSON.parse(cached)
        }
        
        throw error
      } finally {
        this.loading = false
      }
    },

    async fetchPreferences(force = false) {
      if (!force && Date.now() - this.preferencesLastFetch < this.preferencesCacheDuration) {
        return
      }

      try {
        const response = await authenticatedFetch(
          backendURL('companies/preferences'),
          { 
            credentials: 'include',
            cache: 'no-store'
          }
        )
        
        if (!response.ok) {
          throw new Error('Failed to fetch preferences')
        }
        
        const data = await response.json()
        this.preferences = data.preferences
        this.preferencesLastFetch = Date.now()
      } catch (error) {
        console.error('Failed to fetch preferences:', error)
        if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
          this.clearCache()
          this.catalog = []
        } else {
          throw error
        }
      }
    },

    async fetchCompanyStatic(companyRoute: string, force = false): Promise<CompanyWithTests> {
      const cached = this.companyDetailsCache.get(companyRoute)
      const cacheTime = this.companyDetailsCacheTime.get(companyRoute) ?? 0
      
      if (!force && cached && Date.now() - cacheTime < this.companyDetailsCacheDuration) {
        return cached
      }
      
      try {
        const response = await authenticatedFetch(
          backendURL(`companies/${companyRoute}/static`),
          { credentials: 'include' }
        )
        
        if (!response.ok) {
          throw new Error('Failed to fetch company details')
        }
        
        const staticData: CompanyWithTests = await response.json()
        this.companyDetailsCache.set(companyRoute, staticData)
        this.companyDetailsCacheTime.set(companyRoute, Date.now())
        
        return staticData
        
      } catch (error) {
        if (cached) {
          console.warn('Using stale cache due to error:', error)
          return cached
        }
        throw error
      }
    },

    async fetchCompanyPreferences(companyRoute: string): Promise<UserPreferences> {
      const response = await authenticatedFetch(
        backendURL(`companies/${companyRoute}/preferences`),
        { 
          credentials: 'include',
          cache: 'no-store'
        }
      )
      
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied')
        }
        throw new Error('Failed to fetch preferences')
      }
      
      const prefs: UserPreferences = await response.json()
      
      this.preferences[prefs.company_id] = {
        favourite: prefs.favourite,
        permitted: prefs.permitted
      }
      
      return prefs
    },

    async toggleFavourite(companyRoute: string) {
      const company = this.catalog.find(c => c.route === companyRoute) 
        || this.companyDetailsCache.get(companyRoute)
      
      if (!company) {
        throw new Error('Company not found')
      }
      
      const currentFav = this.preferences[company.id]?.favourite ?? false
      const newFav = !currentFav

      this.preferences[company.id] = {
        ...this.preferences[company.id],
        favourite: newFav
      }
      
      try {
        const method = newFav ? 'PUT' : 'DELETE'
        const response = await authenticatedFetch(
          backendURL(`companies/favourite/${companyRoute}`),
          { 
            method, 
            credentials: 'include',
            cache: 'no-store'
          }
        )
        
        if (!response.ok) {
          throw new Error('Failed to update favourite')
        }
        await this.fetchPreferences(true)
        
      } catch (error) {
        this.preferences[company.id] = {
          ...this.preferences[company.id],
          favourite: currentFav
        }
        throw error
      }
    },

    async loadAllData() {
      await Promise.all([
        this.fetchCatalog(),
        this.fetchPreferences()
      ])
    },

    hydrateStorage() {
      try {
        const cached = localStorage.getItem('companies_catalog')
        const timestamp = localStorage.getItem('companies_catalog_timestamp')
        
        if (cached && timestamp) {
          const parsedTimestamp = parseInt(timestamp)
          const maxAge = 24 * 60 * 60 * 1000
          if (Date.now() - parsedTimestamp > maxAge) {
            localStorage.removeItem('companies_catalog')
            localStorage.removeItem('companies_catalog_timestamp')
            return false
          }
          
          this.catalog = JSON.parse(cached)
          this.catalogLastFetch = parsedTimestamp
          return true
        }
        return false
        
      } catch (error) {
        console.error('Failed to hydrate from localStorage:', error)
        localStorage.removeItem('companies_catalog')
        localStorage.removeItem('companies_catalog_timestamp')
        return false
      }
    },

    clearCache() {
      this.catalogLastFetch = 0
      this.preferencesLastFetch = 0
      this.companyDetailsCache.clear()
      this.companyDetailsCacheTime.clear()
      this.preferences = {}
      this.catalog = []
    
      localStorage.removeItem('companies_catalog')
      localStorage.removeItem('companies_catalog_timestamp')
    }
  }
})